import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const itServices = [
  { icon: 'ri-customer-service-2-line', title: 'System Support', description: 'Round-the-clock technical assistance ensuring your systems run smoothly with minimal downtime.' },
  { icon: 'ri-server-line', title: 'Infrastructure Management', description: 'Comprehensive management of IT infrastructure including servers, networks, and data centers.' },
  { icon: 'ri-cloud-line', title: 'Cloud Solutions', description: 'Scalable cloud migration and management services leveraging AWS, Azure, and Google Cloud.' },
  { icon: 'ri-shield-check-line', title: 'Cybersecurity', description: 'Advanced security solutions protecting your digital assets with proactive monitoring.' },
  { icon: 'ri-rocket-2-line', title: 'Digital Transformation', description: 'End-to-end digital transformation services to modernize operations and accelerate growth.' },
  { icon: 'ri-lightbulb-flash-line', title: 'Technology Advisory', description: 'Strategic guidance on technology adoption, system integration, and digital innovation.' },
];

const consultancyServices = [
  { icon: 'ri-lightbulb-line', title: 'Strategic Planning', description: 'Develop comprehensive strategies aligned with your business objectives and long-term vision.' },
  { icon: 'ri-briefcase-line', title: 'Business Advisory', description: 'Expert guidance on business operations, market expansion, and organizational structure.' },
  { icon: 'ri-settings-3-line', title: 'Operations Management', description: 'Optimize operational processes, improve efficiency, and reduce costs through systematic analysis.' },
  { icon: 'ri-shield-check-line', title: 'Risk Management', description: 'Identify, assess, and mitigate business risks through comprehensive frameworks.' },
  { icon: 'ri-refresh-line', title: 'Change Management', description: 'Guide your organization through transformational changes with structured approaches.' },
  { icon: 'ri-line-chart-line', title: 'Performance Optimization', description: 'Enhance business performance through KPI tracking and data-driven decision-making.' },
];

function ServiceCard({ icon, title, description }: { icon: string; title: string; description: string }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className={`relative p-6 rounded-xl border-2 transition-all duration-300 cursor-pointer overflow-hidden group ${
        hovered
          ? 'bg-gradient-to-br from-teal-500 to-teal-600 border-teal-500 text-white -translate-y-2 shadow-2xl shadow-teal-200'
          : 'bg-white border-gray-100 shadow-sm hover:border-teal-200'
      }`}
    >
      {/* 背景光晕 */}
      <div className={`absolute inset-0 rounded-xl transition-opacity duration-300 ${hovered ? 'opacity-100' : 'opacity-0'}`}
        style={{ background: 'radial-gradient(circle at 80% 20%, rgba(255,255,255,0.15) 0%, transparent 60%)' }}
      />

      {/* 图标区域 */}
      <div className={`w-12 h-12 flex items-center justify-center rounded-lg mb-4 transition-all duration-300 ${
        hovered ? 'bg-white/20 scale-110' : 'bg-teal-50 scale-100'
      }`}>
        <i className={`${icon} text-2xl transition-all duration-300 ${hovered ? 'text-white' : 'text-teal-600'}`}></i>
      </div>

      <h4 className={`text-base font-semibold mb-2 transition-colors duration-300 ${hovered ? 'text-white' : 'text-gray-900'}`}>
        {title}
      </h4>
      <p className={`text-sm leading-relaxed transition-colors duration-300 ${hovered ? 'text-white/85' : 'text-gray-500'}`}>
        {description}
      </p>

      {/* 悬停时出现的箭头 */}
      <div className={`mt-4 flex items-center gap-1 text-sm font-medium transition-all duration-300 ${
        hovered ? 'opacity-100 translate-x-0 text-white' : 'opacity-0 -translate-x-2'
      }`}>
        <span>Learn More</span>
        <i className="ri-arrow-right-line text-base"></i>
      </div>

      {/* 底部装饰线 */}
      <div className={`absolute bottom-0 left-0 h-0.5 bg-white/40 transition-all duration-500 ${hovered ? 'w-full' : 'w-0'}`} />
    </div>
  );
}

export default function Services() {
  const navigate = useNavigate();

  return (
    <section id="services" className="pt-6 pb-14 md:pb-24 bg-white">
      <div className="max-w-7xl mx-auto px-5 md:px-6 space-y-10 md:space-y-16">

        {/* IT Services Block */}
        <div id="it-services">
          <div className="flex flex-col items-center text-center mb-8 md:mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-blue-500 mb-4 md:mb-5">
              <i className="ri-computer-line text-blue-600 text-sm"></i>
              <span className="text-blue-600 text-sm font-medium whitespace-nowrap">IT Services</span>
            </div>
            <h2 className="text-xl md:text-3xl font-bold text-gray-900 mb-2 md:mb-3 leading-tight">
              Comprehensive Technology Solutions
            </h2>
            <p className="text-sm text-gray-500 max-w-xl">
              Empowering your business with cutting-edge infrastructure, expert support, and strategic digital transformation.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {itServices.map((s, i) => <ServiceCard key={i} {...s} />)}
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-gray-100"></div>

        {/* Consultancy Services Block */}
        <div id="consultancy-services">
          <div className="flex flex-col items-center text-center mb-8 md:mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-blue-500 mb-4 md:mb-5">
              <i className="ri-lightbulb-line text-blue-600 text-sm"></i>
              <span className="text-blue-600 text-sm font-medium whitespace-nowrap">Consultancy Services</span>
            </div>
            <h2 className="text-xl md:text-3xl font-bold text-gray-900 mb-2 md:mb-3 leading-tight">
              Strategic Business Advisory
            </h2>
            <p className="text-sm text-gray-500 max-w-xl">
              Expert guidance to navigate complex challenges, optimize operations, and achieve sustainable growth.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {consultancyServices.map((s, i) => <ServiceCard key={i} {...s} />)}
          </div>
        </div>

      </div>
    </section>
  );
}